// How long a stop takes once the driver is parked.
//
// CommonJS for the same reason bookingRules.js is: scripts/check-travel.js
// requires it with no transpiler. Pure -- no Firestore, no Vue, no clock.
//
// The rule, as delivery specified it:
//
//   1 ชิ้น        30 นาที
//   2-3 ชิ้น      45 นาที
//   4 ชิ้น        60 นาที
//   เกิน 4 ชิ้น    +15 นาที ต่อชิ้น
//
// The tiers are a table rather than a chain of ifs so the operation can retune
// them without touching logic -- these numbers came from experience and will
// move as the product mix does.
//
// The last line matters more than it looks. The original rule stopped at four
// items, which silently makes a 12-item order cost the same hour as a 4-item
// one; a planner believing that would build a day it cannot finish. At
// +15/item a 12-item job is 180 minutes -- most of a car's usable afternoon,
// and worth showing staff at booking time rather than discovering at 16:00.

// Each tier: up to `items` inclusive, takes `minutes`. Must stay sorted.
const TIERS = [
  { items: 1, minutes: 30 },
  { items: 3, minutes: 45 },
  { items: 4, minutes: 60 }
]

// Applied to every item beyond the last tier.
const PER_EXTRA_ITEM = 15

// The tier a job falls into when nobody has said how many items it has. A
// booking made as นัดหมาย carries no SO yet, so its item count is genuinely
// unknown until the SO lands 1-3 days before delivery. Planning it as the
// smallest possible job would build a schedule that breaks the moment the real
// count arrives, so the assumption is the common case instead: 2-3 items.
const DEFAULT_ITEMS = 2

// A count arrives from a text field or from counting SO lines, so it can be a
// string, a float, null, or nonsense. Anything unusable becomes DEFAULT_ITEMS
// rather than zero -- a stop still happens, and a zero-minute stop is the one
// answer guaranteed to be wrong.
//
// Zero is treated as unknown, not as "no work": a booking exists because
// something is being delivered.
function normalizeItems (items) {
  if (items == null || items === '') return DEFAULT_ITEMS
  const n = Math.floor(Number(items))
  if (!isFinite(n) || n <= 0) return DEFAULT_ITEMS
  return n
}

// ---------------------------------------------------------------------------
// Reading a count out of รายการสินค้า
// ---------------------------------------------------------------------------
//
// The authoritative count is the number of lines on the SO. This is the
// fallback for a booking that has no SO yet, or one written before the form had
// a จำนวนชิ้น field -- and it is a HEURISTIC, which is why it reports its own
// confidence instead of just a number.
//
// The text is free-form and typed by staff. What it looks like in practice:
//
//   "ตู้คีออสสั่งอาหาร 32 นิ้ว 1 เครื่อง"   -> 1   (32 is a SIZE, not a count)
//   "ตู้คีออสสั่งอาหาร 43 นิ้ว 2 เครื่อง"   -> 2
//   "เครื่องพิมพ์ใบเสร็จ 3 เครื่อง"          -> 3
//   "ขาตั้งจอสัมผัส 21 นิ้ว 1 ชุด"           -> 1
//   "ตู้"                                    -> 1, but not confidently
//
// The trap is the measurement: a naive "first number in the line" reads 32 นิ้ว
// as thirty-two kiosks and turns a half-hour job into an eight-hour one. So a
// number only counts when the word after it is a unit of QUANTITY, and the last
// such match in the line wins.

// A number followed by one of these is a count.
const QUANTITY_UNITS = [
  'เครื่อง', 'ชุด', 'ตัว', 'ใบ', 'อัน', 'ชิ้น', 'กล่อง', 'ลัง', 'คู่', 'แผ่น', 'ม้วน', 'ถัง', 'หลัง', 'ตู้'
]

// A number followed by one of these is a dimension, and must never be counted.
const MEASURE_UNITS = [
  'นิ้ว', 'ซม', 'ซม.', 'เซนติเมตร', 'เมตร', 'ม.', 'มม', 'มม.', 'กก', 'กก.', 'กิโล', 'กิโลกรัม',
  'cm', 'mm', 'm', 'kg', 'inch', 'in'
]

// A line is worth counting if it has any Thai or Latin letter in it. A line of
// punctuation or a stray number is not a product.
const HAS_TEXT = /[ก-๙a-zA-Z]/

function splitLines (text) {
  return String(text)
    .split(/[\n\r;,•]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0)
}

// Returns { items, confident, lines }.
//
// `confident` is false when any line had to be assumed as one -- the caller
// should treat the number as a starting point staff may need to correct, and
// dispatchPlan flags such a stop rather than presenting the duration as known.
function countFromProductList (text) {
  if (!text || typeof text !== 'string') return { items: 0, confident: false, lines: 0 }

  const lines = splitLines(text)
  if (!lines.length) return { items: 0, confident: false, lines: 0 }

  let total = 0
  let assumed = 0
  let counted = 0

  lines.forEach(line => {
    if (!HAS_TEXT.test(line)) return
    counted++

    // Every "<number> <word>" in the line, in order.
    const re = /(\d+)\s*([ก-๙a-zA-Z.]*)/g
    let match
    let found = null
    while ((match = re.exec(line)) !== null) {
      const value = Number(match[1])
      const unit = (match[2] || '').trim()
      if (!isFinite(value) || value <= 0) continue
      if (MEASURE_UNITS.indexOf(unit) !== -1) continue
      if (QUANTITY_UNITS.indexOf(unit) === -1) continue
      // Last quantity match wins: "43 นิ้ว 2 เครื่อง" is two kiosks, not
      // forty-three.
      found = value
    }

    if (found == null) {
      // A product named with no quantity is one of it -- the common way a
      // single item gets typed.
      total += 1
      assumed++
    } else {
      total += found
    }
  })

  if (!counted) return { items: 0, confident: false, lines: 0 }
  return { items: total, confident: assumed === 0, lines: counted }
}

// ---------------------------------------------------------------------------
// SAP product rows
// ---------------------------------------------------------------------------
//
// GetProdList returns one row per SO line: qty / prodCode / prodName / front /
// frame / volume. Three things read those rows -- the total stored on the
// booking, the breakdown the edit dialog shows staff, and the free text
// รายการสินค้า is seeded with -- and a booking where those three disagree is
// worse than one with no breakdown at all, so all three come through here
// rather than each re-reading the row its own way.

// In field-name order of preference. The first three were guesses made before
// anyone had seen a real response; qty is the one SAP actually sends, and the
// others stay only because old responses may not.
const QTY_FIELDS = ['qty', 'quantity', 'quan']
const NAME_FIELDS = ['prodName', 'itemName', 'dscription', 'prodCode']

function productQty (row) {
  const r = row || {}
  for (let i = 0; i < QTY_FIELDS.length; i++) {
    const v = r[QTY_FIELDS[i]]
    if (v == null || v === '') continue
    const n = Math.floor(Number(v))
    if (isFinite(n) && n > 0) return n
  }
  return 0
}

function productName (row) {
  const r = row || {}
  for (let i = 0; i < NAME_FIELDS.length; i++) {
    const v = r[NAME_FIELDS[i]]
    if (v != null && String(v).trim()) return String(v).trim()
  }
  return ''
}

// The SAP item code (GetProdList's `prodCode`), '' when the row has none.
// productName falls back to it for a row with no description, so a caller
// printing both must not print the code twice -- see productLabel.
const CODE_FIELDS = ['prodCode', 'itemCode']
function productCode (row) {
  const r = row || {}
  for (let i = 0; i < CODE_FIELDS.length; i++) {
    const v = r[CODE_FIELDS[i]]
    if (v != null && String(v).trim()) return String(v).trim()
  }
  return ''
}

// A line that is labour, not goods: SAP keys a repair or adjustment visit
// (SO 226090121, "ค่าบริการ") as item SERVICE, with no volume. Such a line
// takes no space on a truck -- the products it concerns are already at the
// customer's -- so the up-country figures count it as nothing. Add a code here
// if SAP gains another non-stock item.
const SERVICE_CODES = ['SERVICE']
function isServiceRow (row) {
  return SERVICE_CODES.indexOf(productCode(row).toUpperCase()) !== -1
}

// "<code> <description>", as staff read an SO line.
function productLabel (row) {
  const code = productCode(row)
  const name = productName(row)
  if (!code || code === name) return name
  return name ? code + ' ' + name : code
}

// A line SAP gave no quantity for is one of it -- the same reading
// countFromProductList gives a product named with no number, and for the same
// reason: the line exists because something is being delivered.
function itemsFromRows (rows) {
  if (!Array.isArray(rows)) return 0
  return rows.reduce((sum, r) => sum + (productQty(r) || 1), 0)
}

// An SO's volume in m3. `itemVolume` is the item master's CURRENT per-piece
// volume (GetProdList reads OITM.SVolume), so it is multiplied by qty; a line
// without one falls back to its own `volume`, as before. The line value is frozen
// at SO creation and a set's pieces each carried the whole set's volume (SO
// 126100047: PT-04/B and PT-04/L at 0.06 for a 0.06 m3 set, summed as 0.126).
function volumeFromRows (rows) {
  if (!Array.isArray(rows)) return 0
  const total = rows.reduce((sum, r) => {
    const piece = Number(r && r.itemVolume)
    if (piece > 0) return sum + piece * (productQty(r) || 1)
    return sum + (Number(r && r.volume) || 0)
  }, 0)
  return Math.round(total * 10000) / 10000
}

// The free text a booking is seeded with when its SO is looked up.
//
// The quantity is appended in a unit countFromProductList recognises, so text
// generated here parses back to the number it was generated from -- asserted as
// a round trip in check-travel.js. Names alone are not enough and the failure is
// silent: SO 16090035 is two lines for three items, so the seeded text read as
// two while จำนวนชิ้น beside it said three, and neither number explained itself.
//
// Appending is safe against names that already carry a number -- "ตู้เก็บของ
// 5 ลิ้นชัก KOL 2 เครื่อง" reads as 2, because ลิ้นชัก counts nothing and the
// last quantity match in a line wins.
const TEXT_QUANTITY_UNIT = 'เครื่อง'

function productListText (rows) {
  if (!Array.isArray(rows)) return ''
  return rows.map(row => {
    const name = productName(row)
    if (!name) return ''
    const qty = productQty(row)
    return qty > 0 ? name + ' ' + qty + ' ' + TEXT_QUANTITY_UNIT : name
  }).filter(Boolean).join('\n')
}

// The best item count available for a booking, and where it came from.
//
// Order is not negotiable. An explicit count -- from SO lines, or typed into the
// form -- beats anything read out of free text, which beats the default tier.
// `source` travels with the number so a plan can show which durations are
// measured and which are assumed.
function itemsForBooking (booking) {
  const b = booking || {}

  const explicit = b.itemCount != null ? b.itemCount : b.items
  if (explicit != null && explicit !== '') {
    const n = Math.floor(Number(explicit))
    if (isFinite(n) && n > 0) return { items: n, source: 'explicit', confident: true }
  }

  const parsed = countFromProductList(b.productList)
  if (parsed.items > 0) {
    return { items: parsed.items, source: 'productList', confident: parsed.confident }
  }

  return { items: DEFAULT_ITEMS, source: 'default', confident: false }
}

function installMinutes (items) {
  const n = normalizeItems(items)

  for (let i = 0; i < TIERS.length; i++) {
    if (n <= TIERS[i].items) return TIERS[i].minutes
  }

  const top = TIERS[TIERS.length - 1]
  return top.minutes + (n - top.items) * PER_EXTRA_ITEM
}

// ---------------------------------------------------------------------------
// The duration a stop is actually planned for
// ---------------------------------------------------------------------------
//
// The tiers above are an average across a product mix. Some models go in
// faster than the average and some fight back, and the person who has
// installed one knows which -- so staff can set the duration directly and the
// planner uses what they said.
//
// The bounds are not UI politeness. Firestore rules are wide open, so the
// booking form is not the only way a number reaches this function. A stop of 0
// minutes and a stop of 6000 both build a day that cannot happen, and this is
// the last place that can refuse either. MIN sits below the smallest tier and
// MAX is a working day, so clamping can never contradict the table above.
const MIN_INSTALL_MINUTES = 15
const MAX_INSTALL_MINUTES = 480

function clampMinutes (m) {
  if (m < MIN_INSTALL_MINUTES) return MIN_INSTALL_MINUTES
  if (m > MAX_INSTALL_MINUTES) return MAX_INSTALL_MINUTES
  return m
}

// Returns { minutes, source, confident }.
//
// `source` is 'manual' when staff set the duration themselves; otherwise it is
// the ITEM source from itemsForBooking, because that is what a plan needs in
// order to say whether a duration was counted or assumed.
//
// A manual duration is confident whatever the item count underneath it looks
// like. That is the whole point: once a human has said how long the stop takes,
// the timetable no longer rests on a guessed count, and a planner that kept
// warning about the guess would be pointing at a number nothing depends on.
// A return (RT) -- see returnJob.js, which re-exports this. Kept here rather
// than required from there because returnJob already requires this file.
const RETURN_MINUTES = 30

function minutesForBooking (booking) {
  const b = booking || {}

  // Blank, null, 0 and nonsense all mean "nobody set one" -- the same reading
  // normalizeItems gives a missing count, and for the same reason.
  const typed = Math.floor(Number(b.installMinutes))
  if (isFinite(typed) && typed > 0) {
    return { minutes: clampMinutes(typed), source: 'manual', confident: true, returnMinutes: 0 }
  }

  // A return-only pickup removes items; it installs nothing, so the tiers --
  // and their default for an unknown count -- do not apply.
  if (b.jobType === 'return') {
    return { minutes: RETURN_MINUTES, source: 'return', confident: true, returnMinutes: RETURN_MINUTES }
  }

  const count = itemsForBooking(b)
  const extra = b.jobType === 'exchange' ? RETURN_MINUTES : 0
  return {
    minutes: clampMinutes(installMinutes(count.items) + extra),
    source: count.source,
    confident: count.confident,
    returnMinutes: extra
  }
}

// Whether the vehicle is parked long enough for a 30-minute charge to fit
// inside the stop. This is the cheapest energy the operation has: the driver is
// working, the car is idle, and the charge costs nothing in schedule time.
//
// It is why 4ฒง-7864 (140 km) can reach บางนา and สมุทรปราการ at all -- those
// are 143 and 160 km round trips, past its range on a single charge. See the
// range table in scripts/README.md.
const CHARGE_MINUTES = 30

// There is deliberately no fitsChargeDuringInstall() here any more, and this is
// the reason it should not come back.
//
// It answered "is this stop long enough to hold a 30-minute charge", and the
// planner used it to charge an EV for free during a big installation. The
// arithmetic was right and the premise was wrong: THE DRIVER INSTALLS. They are
// part of the crew, working alongside the support staff, not sitting in the car
// while it charges -- so the car is at the customer's address for the whole
// installation and cannot be at a station.
//
// Lunch is the one window that genuinely absorbs a charge, because the driver
// can eat at the station. That lives in chargers.js, which is where the
// question belongs.

// Thai label for the booking form, so staff see the cost of a big order while
// they are still choosing the date.
function describe (items) {
  const n = normalizeItems(items)
  const m = installMinutes(n)
  const hours = Math.floor(m / 60)
  const mins = m % 60
  const time = hours
    ? (mins ? `${hours} ชม. ${mins} นาที` : `${hours} ชม.`)
    : `${mins} นาที`
  return `${n} ชิ้น ~ ${time}`
}

module.exports = {
  volumeFromRows,
  TIERS,
  QUANTITY_UNITS,
  MEASURE_UNITS,
  countFromProductList,
  TEXT_QUANTITY_UNIT,
  productQty,
  productName,
  productCode,
  SERVICE_CODES,
  isServiceRow,
  productLabel,
  itemsFromRows,
  productListText,
  itemsForBooking,
  PER_EXTRA_ITEM,
  DEFAULT_ITEMS,
  CHARGE_MINUTES,
  MIN_INSTALL_MINUTES,
  MAX_INSTALL_MINUTES,
  normalizeItems,
  clampMinutes,
  installMinutes,
  minutesForBooking,
  RETURN_MINUTES,
  describe
}
